/**
 * The fade of the whole page to black and back (ADR-122 decision 2.9, the author's ruling of 2026-10-29: the HUD fades
 * with the world): one black layer over the page, made on first use, that takes no pointer and no focus, so a chat input
 * that is open keeps what was typed and stays usable under it. The layer stands in the page's UI root just before the
 * loading shell, so it paints over the world, HUD, chat and dialogue and under the loading shell, the menus and the login.
 */

let layer: HTMLDivElement | null = null;

/**
 * The page's fade layer, made when first asked for.
 *
 * @returns The layer.
 */
function fadeLayer(): HTMLDivElement {
  if (layer === null || !layer.isConnected) {
    layer = document.createElement("div");
    layer.setAttribute("data-screen-fade", "");
    layer.setAttribute("aria-hidden", "true");
    layer.style.cssText = "position:fixed;inset:0;background:#000;opacity:0;pointer-events:none";
    const shell = document.getElementById("loading");
    if (shell?.parentElement) {
      shell.parentElement.insertBefore(layer, shell);
    } else {
      document.body.appendChild(layer);
    }
  }
  return layer;
}

/**
 * Fades the page to black or back from it.
 *
 * @param direction `out` to black, `in` back to the page.
 * @param ms The fade's length in milliseconds; under one frame is one millisecond.
 * @returns A promise that settles when the fade is done.
 */
export function fadeScreen(direction: "out" | "in", ms: number): Promise<void> {
  const element = fadeLayer();
  const length = Math.max(1, ms);
  return new Promise((resolve) => {
    element.style.transition = `opacity ${length}ms linear`;
    void element.offsetWidth;
    element.style.opacity = direction === "out" ? "1" : "0";
    setTimeout(resolve, length);
  });
}
